// Ops · Ads (docs/HOME-ADS-CONTRACT.md §2-3): the Home top-slot ads. The client for the five Ops
// endpoints, the form's rules (the server is the gatekeeper; these mirror it so the screen can say
// what's missing before anyone clicks), the upload checks, the schedule in Manila time, the rotation
// slots and the stats. Pure (no DOM, no Intl; fetch and token come in as arguments): node --test runs it.
import { ApiRequestError } from '@popcart/core/v1';

/** @typedef {import('./types').OpsAd} OpsAd */
/** @typedef {import('./types').OpsAdInput} OpsAdInput */
/** @typedef {import('./types').OpsAdPatch} OpsAdPatch */
/** @typedef {import('./types').AdPlate} AdPlate */
/** @typedef {import('./types').AdCategory} AdCategory */
/** @typedef {import('./types').AdLinkKind} AdLinkKind */
/** @typedef {import('./types').AdStatus} AdStatus */
/** @typedef {import('./types').AdKind} AdKind */

// ── limits (contract §1-2 and the advertiser spec sheet) ─────────────────────────────────────
export const MAX_ADVERTISER = 60;
export const MAX_HEADLINE = 25;
export const MAX_DEAL = 8;
export const MAX_PERMIT = 60;
export const MAX_CLEARANCE = 40;
export const MAX_IMAGE_BYTES = 300 * 1024;
export const ADVISED_IMAGE_BYTES = 150 * 1024;
export const ADVISED_IMAGE = { width: 1200, height: 628 };
export const MAX_VIDEO_BYTES = 1_126_400;
export const VIDEO_SECONDS = { min: 6, max: 15 };
export const IMAGE_TYPES = /** @type {const} */ (['image/jpeg', 'image/png', 'image/webp']);
/** Up to this many ads rotate in the Home slot (contract: "max 3"). */
export const SLOTS = 3;

export const adsKeys = {
  all: /** @type {const} */ (['ops', 'ads']),
  list: /** @type {const} */ (['ops', 'ads', 'list']),
};

// ── vocabularies ─────────────────────────────────────────────────────────────────────────────
/** @type {{ value: AdCategory; label: string }[]} */
export const CATEGORY_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'food', label: 'Food' },
  { value: 'grocery', label: 'Grocery' },
  { value: 'alcohol', label: 'Alcohol' },
  { value: 'supplement', label: 'Food supplement' },
  { value: 'otc', label: 'OTC medicine' },
];

/** Never accepted at launch (contract §2 BLOCKED_CATEGORIES): shown greyed out, never sent. */
export const BLOCKED_CATEGORIES = Object.freeze([
  'Vapes & tobacco',
  'Gambling & e-sabong',
  'Political',
  'Prescription drugs',
  'Breast-milk substitutes',
  'Adult content',
]);

/** Categories that need an ASC clearance code before they go live. */
export const GATED_CATEGORIES = /** @type {readonly AdCategory[]} */ (Object.freeze(['alcohol', 'supplement', 'otc']));

/** Plate colour → the kernel token the card uses, and the word Ops sees. */
export const PLATES = /** @type {Readonly<Record<AdPlate, { label: string; token: string; dark: boolean }>>} */ (
  Object.freeze({
    volt: { label: 'Pop Yellow', token: '--pc-palette-core-volt', dark: false },
    cream: { label: 'Cream', token: '--pc-palette-core-cream', dark: false },
    ultra: { label: 'Cart Blue', token: '--pc-palette-core-ultra', dark: true },
    ink: { label: 'Ink', token: '--pc-palette-core-ink', dark: true },
    kiwi: { label: 'Kiwi', token: '--pc-palette-category-fresh', dark: false },
    ember: { label: 'Ember', token: '--pc-palette-category-grill', dark: false },
    sky: { label: 'Sky', token: '--pc-palette-category-mart', dark: false },
    gum: { label: 'Gum', token: '--pc-palette-category-creators-live', dark: false },
  })
);
export const PLATE_KEYS = /** @type {AdPlate[]} */ (Object.keys(PLATES));

/** @type {{ value: AdLinkKind; label: string }[]} */
export const LINK_OPTIONS = [
  { value: 'none', label: 'Nothing (no link)' },
  { value: 'shop', label: 'A shop page' },
  { value: 'product', label: 'A product' },
  { value: 'line', label: 'A PopCart line' },
];
/** @type {{ value: string; label: string }[]} */
export const LINE_TARGETS = [
  { value: 'fresh', label: 'Fresh' },
  { value: 'grill', label: 'Grill' },
  { value: 'mart', label: 'Grocery' },
  { value: 'express', label: 'Express' },
];

export const STATUS_VIEW = /** @type {const} */ ({
  draft: { label: 'Draft', tone: 'neutral' },
  live: { label: 'Live', tone: 'success' },
  paused: { label: 'Paused', tone: 'warning' },
  archived: { label: 'Archived', tone: 'neutral' },
});

// ── client ───────────────────────────────────────────────────────────────────────────────────
export const OPS_ADS_PATHS = {
  list: () => '/ops/ads',
  /** @param {string} id */
  one: (id) => `/ops/ads/${encodeURIComponent(id)}`,
  /** @param {string} id */
  image: (id) => `/ops/ads/${encodeURIComponent(id)}/image`,
  /** @param {string} id */
  video: (id) => `/ops/ads/${encodeURIComponent(id)}/video`,
};

const NETWORK = () => new ApiRequestError(0, /** @type {any} */ ({ error: "Can't reach PopCart. Check your connection.", code: 'network' }));

/**
 * A non-2xx as the core client would throw it (and the 401 hook). A 409 `not_ready` keeps its
 * `missing[]` on the error (`err.missing`), which the core error class doesn't carry.
 * @param {Response} res @param {(() => void) | undefined} onUnauthorized
 */
async function failure(res, onUnauthorized) {
  if (res.status === 401) onUnauthorized?.();
  /** @type {any} */
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const err = /** @type {ApiRequestError & { missing?: string[] }} */ (new ApiRequestError(res.status, body));
  if (body && Array.isArray(body.missing)) err.missing = body.missing.map(String);
  return err;
}

/**
 * The Ops ads endpoints in the core client's shape (bearer token, JSON, ApiRequestError). The core
 * client doesn't carry them; src/api.ts merges these under it, so a core method of the same name wins.
 * @param {{ baseUrl?: string; getToken: () => string | null; fetchImpl?: typeof fetch; onUnauthorized?: () => void }} opts
 */
export function createOpsAdsApi({ baseUrl = '', getToken, fetchImpl, onUnauthorized }) {
  const doFetch = fetchImpl ?? ((/** @type {Parameters<typeof fetch>} */ ...a) => fetch(...a));
  const root = baseUrl.replace(/\/+$/, '') + '/api/v1';
  /** @param {string} method @param {string} path @param {unknown} [body] */
  async function call(method, path, body) {
    /** @type {Record<string, string>} */
    const headers = { Accept: 'application/json' };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const multipart = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
    let res;
    try {
      res = await doFetch(root + path, { method, headers, body: body === undefined ? undefined : multipart ? /** @type {FormData} */ (body) : JSON.stringify(body) });
    } catch {
      throw NETWORK();
    }
    if (!res.ok) throw await failure(res, onUnauthorized);
    return res.json();
  }
  return {
    /** @returns {Promise<{ items: OpsAd[] }>} */
    opsAds: () => call('GET', OPS_ADS_PATHS.list()),
    /** @param {OpsAdInput} input @returns {Promise<OpsAd>} */
    createOpsAd: (input) => call('POST', OPS_ADS_PATHS.list(), input),
    /** @param {string} id @param {OpsAdPatch} patch @returns {Promise<OpsAd>} */
    updateOpsAd: (id, patch) => call('PATCH', OPS_ADS_PATHS.one(id), patch),
    /** multipart field `image` @param {string} id @param {FormData} form @returns {Promise<OpsAd>} */
    uploadOpsAdImage: (id, form) => call('POST', OPS_ADS_PATHS.image(id), form),
    /** multipart field `video` @param {string} id @param {FormData} form @returns {Promise<OpsAd>} */
    uploadOpsAdVideo: (id, form) => call('POST', OPS_ADS_PATHS.video(id), form),
  };
}

/** The `missing[]` of a 409 not_ready, or null for any other error. @param {unknown} err */
export function notReadyOf(err) {
  const e = /** @type {{ status?: number; code?: string; missing?: unknown; details?: any } | null} */ (err);
  if (!e || e.status !== 409) return null;
  const list = Array.isArray(e.missing) ? e.missing : Array.isArray(e.details?.missing) ? e.details.missing : null;
  if (e.code !== 'not_ready' && !list) return null;
  return (list ?? []).map(String);
}

// ── uploads ──────────────────────────────────────────────────────────────────────────────────
/** "300 KB", "1.1 MB". @param {number} bytes */
export function fileSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(Math.round((bytes / (1024 * 1024)) * 10) / 10).toFixed(1)} MB`;
}

const extOf = (/** @type {string} */ name) => (/\.([a-z0-9]+)$/i.exec(name ?? '')?.[1] ?? '').toLowerCase();

/**
 * Why this photo can't be uploaded, or null. jpg/png/webp, ≤ 300 KB (served as-is).
 * @param {{ name?: string; type?: string; size: number }} file
 */
export function imageProblem(file) {
  const type = (file.type || '').toLowerCase();
  const ext = extOf(file.name ?? '');
  const okType = type ? /** @type {readonly string[]} */ (IMAGE_TYPES).includes(type) : ['jpg', 'jpeg', 'png', 'webp'].includes(ext);
  if (!okType) return 'Use a JPG, PNG or WebP photo.';
  if (file.size > MAX_IMAGE_BYTES) return `This photo is ${fileSize(file.size)}. The limit is 300 KB: export it at 1200 × 628 as WebP (150 KB or less is best).`;
  return null;
}

/**
 * Advice (not a block) for a photo that will upload: the size and shape the card is built for.
 * @param {{ size: number }} file @param {{ width: number; height: number } | null} [dims]
 */
export function imageAdvice(file, dims) {
  const out = [];
  if (file.size > ADVISED_IMAGE_BYTES) out.push(`${fileSize(file.size)}: 150 KB or less loads faster on mobile data.`);
  if (dims && dims.width > 0 && dims.height > 0) {
    const ratio = dims.width / dims.height;
    if (Math.abs(ratio - 1200 / 628) > 0.06) out.push(`${dims.width} × ${dims.height} isn't 1.91:1. The card crops it; 1200 × 628 fits exactly.`);
    else if (dims.width < 1200) out.push(`${dims.width} × ${dims.height} is small; 1200 × 628 stays sharp on big phones.`);
  }
  return out;
}

/**
 * Why this video can't be uploaded, or null. MP4 only, ≤ 1,126,400 bytes, 6–15 s when the length
 * is known (the browser reads it; null = couldn't tell, the server checks the rest).
 * @param {{ name?: string; type?: string; size: number }} file @param {number | null} [seconds]
 */
export function videoProblem(file, seconds = null) {
  const type = (file.type || '').toLowerCase();
  const okType = type ? type === 'video/mp4' : extOf(file.name ?? '') === 'mp4';
  if (!okType) return 'Use an MP4 video (H.264).';
  if (file.size > MAX_VIDEO_BYTES) return `This video is ${fileSize(file.size)}. The limit is 1.1 MB: 960 × 504, no sound track, 6–10 s usually fits.`;
  if (seconds !== null && Number.isFinite(seconds)) {
    if (seconds < VIDEO_SECONDS.min - 0.25) return `This video is ${seconds.toFixed(1)} s. Ads run 6 to 15 seconds.`;
    if (seconds > VIDEO_SECONDS.max + 0.25) return `This video is ${seconds.toFixed(1)} s. Ads run 6 to 15 seconds; trim it.`;
  }
  return null;
}

/** The multipart body: field `image` or `video`. @param {'image' | 'video'} field @param {Blob & { name?: string }} file */
export function uploadForm(field, file) {
  const form = new FormData();
  const ext = field === 'video' ? 'mp4' : file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
  form.append(field, file, file.name || `ad.${ext}`);
  return form;
}

// ── time (Asia/Manila, UTC+8, no DST) ────────────────────────────────────────────────────────
const MANILA = 8 * 3600 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');

/** ISO → { day: 'YYYY-MM-DD', time: 'HH:MM' } in Manila. @param {string} iso */
export function toManila(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return { day: '', time: '' };
  const d = new Date(t + MANILA);
  return { day: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`, time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}` };
}

/** Manila day + time → ISO (UTC), or null. @param {string} day @param {string} time */
export function fromManila(day, time) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day ?? '');
  const h = /^(\d{2}):(\d{2})$/.exec(time ?? '');
  if (!m || !h) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(h[1]), Number(h[2])) - MANILA;
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

/** "12 Oct, 09:00" in Manila. @param {string} iso */
export function manilaStamp(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const d = new Date(t + MANILA);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** Where the ad sits in its schedule. @param {{ startsAt: string; endsAt: string }} ad @param {number} now */
export function schedulePhase(ad, now) {
  const s = Date.parse(ad.startsAt), e = Date.parse(ad.endsAt);
  if (now < s) return 'upcoming';
  if (now >= e) return 'ended';
  return 'running';
}

/** @param {{ startsAt: string; endsAt: string }} ad @param {number} now */
export function scheduleView(ad, now) {
  const phase = schedulePhase(ad, now);
  return {
    range: `${manilaStamp(ad.startsAt)} → ${manilaStamp(ad.endsAt)}`,
    phase,
    label: phase === 'running' ? 'Running now' : phase === 'upcoming' ? 'Starts later' : 'Ended',
  };
}

/** Default schedule for a new ad: from the next full hour, for 14 days. @param {number} now */
export function defaultSchedule(now) {
  const start = Math.ceil(now / 3600000) * 3600000;
  return { startsAt: new Date(start).toISOString(), endsAt: new Date(start + 14 * 86400000).toISOString() };
}

// ── rotation ─────────────────────────────────────────────────────────────────────────────────
/**
 * Which Home slot each ad holds right now: live, inside its schedule, `priority DESC, created_at ASC`,
 * at most 3 (the server's serving rule). Slot 1 is the premium slot. Map of id → 1..3.
 * @param {OpsAd[]} ads @param {number} now
 */
export function slotsNow(ads, now) {
  const serving = ads
    .filter((a) => a.status === 'live' && schedulePhase(a, now) === 'running')
    .sort((a, b) => b.priority - a.priority || Date.parse(a.createdAt) - Date.parse(b.createdAt));
  /** @type {Map<string, number>} */
  const out = new Map();
  serving.slice(0, SLOTS).forEach((a, i) => out.set(a.id, i + 1));
  return out;
}

/** The list order: non-archived first, then archived; newest first (as the server sends it). @param {OpsAd[]} ads */
export function sortAds(ads) {
  return [...ads].sort((a, b) => Number(a.status === 'archived') - Number(b.status === 'archived') || Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

// ── stats ────────────────────────────────────────────────────────────────────────────────────
/** CTR = taps ÷ viewable impressions, as "2.4%" (one decimal), "—" with no views. @param {{ taps: number; viewable: number }} s */
export function ctr(s) {
  if (!s || !s.viewable) return '—';
  const pct = (s.taps / s.viewable) * 100;
  return `${pct >= 10 ? Math.round(pct) : (Math.round(pct * 10) / 10).toFixed(1)}%`;
}

// ── the permit line on the card ──────────────────────────────────────────────────────────────
/** What the customer app shows under the headline (HomeAd.permit). @param {{ permitText: string | null; clearanceCode: string | null }} a */
export function permitLine(a) {
  const p = (a.permitText ?? '').trim();
  const c = (a.clearanceCode ?? '').trim();
  if (p && c) return `${p} · ASC ${c}`;
  if (p) return p;
  if (c) return `ASC ${c}`;
  return null;
}

// ── go-live rules (the server decides; this mirrors contract §2 so the form can explain) ──────
/**
 * What blocks going live, from what the form knows. Keys match the server's `missing[]` when it
 * uses field names; `missingLabel` turns either into plain words.
 * @param {{ kind: AdKind; category: AdCategory; clearanceCode: string | null; permitText: string | null; dealLabel: string | null; imageUrl: string | null; videoUrl: string | null }} a
 */
export function readiness(a) {
  const has = (/** @type {string | null} */ v) => !!(v && v.trim());
  const items = [
    { key: 'imageUrl', label: a.kind === 'video' ? 'Poster frame uploaded' : 'Photo uploaded', done: has(a.imageUrl) },
  ];
  if (a.kind === 'video') items.push({ key: 'videoUrl', label: 'Video uploaded (MP4, 1.1 MB or less)', done: has(a.videoUrl) });
  if (GATED_CATEGORIES.includes(a.category)) items.push({ key: 'clearanceCode', label: 'ASC clearance code added', done: has(a.clearanceCode) });
  if (a.category === 'supplement') items.push({ key: 'permitText', label: 'FDA warning line (MAHALAGANG PAALALA) in the permit line', done: has(a.permitText) });
  else if (has(a.dealLabel)) items.push({ key: 'permitText', label: 'DTI promo permit in the permit line', done: has(a.permitText) });
  return items;
}

const MISSING_WORDS = /** @type {Record<string, string>} */ ({
  image: 'Upload the photo (for a video, its poster frame).',
  imageurl: 'Upload the photo (for a video, its poster frame).',
  poster: 'Upload the photo (for a video, its poster frame).',
  video: 'Upload the video (MP4, 1.1 MB or less).',
  videourl: 'Upload the video (MP4, 1.1 MB or less).',
  clearance: 'Add the ASC clearance code (alcohol, supplements and OTC medicines need one).',
  clearancecode: 'Add the ASC clearance code (alcohol, supplements and OTC medicines need one).',
  asc: 'Add the ASC clearance code (alcohol, supplements and OTC medicines need one).',
  permit: 'Add the permit line: the DTI promo permit for a deal, or the FDA warning for a supplement.',
  permittext: 'Add the permit line: the DTI promo permit for a deal, or the FDA warning for a supplement.',
  dtipermit: 'Add the DTI promo permit number in the permit line (the ad shows a deal).',
  fdawarning: 'Add the FDA "MAHALAGANG PAALALA" warning in the permit line (supplement ads must carry it).',
  schedule: 'Set an end date that is still to come.',
  endsat: 'Set an end date that is still to come.',
  ended: 'Set an end date that is still to come.',
});

/** One `missing[]` item in plain words (field names are translated; sentences pass through). @param {string} item */
export function missingLabel(item) {
  const key = String(item).toLowerCase().replace(/[^a-z]/g, '');
  return MISSING_WORDS[key] ?? String(item);
}

// ── the form ─────────────────────────────────────────────────────────────────────────────────
/**
 * The form's state: every field a string so inputs stay controlled.
 * @typedef {{ kind: AdKind; advertiserName: string; vendorId: string; headline: string; plate: AdPlate; dealLabel: string; permitText: string; category: AdCategory; clearanceCode: string; linkKind: AdLinkKind; linkTarget: string; startDay: string; startTime: string; endDay: string; endTime: string; priority: string }} AdDraft
 */

/** @param {number} now @returns {AdDraft} */
export function emptyDraft(now) {
  const s = defaultSchedule(now);
  const a = toManila(s.startsAt), b = toManila(s.endsAt);
  return {
    kind: 'photo', advertiserName: '', vendorId: '', headline: '', plate: 'volt', dealLabel: '', permitText: '', category: 'general', clearanceCode: '',
    linkKind: 'none', linkTarget: '', startDay: a.day, startTime: a.time, endDay: b.day, endTime: b.time, priority: '0',
  };
}

/** @param {OpsAd} ad @returns {AdDraft} */
export function draftOf(ad) {
  const a = toManila(ad.startsAt), b = toManila(ad.endsAt);
  return {
    kind: ad.kind, advertiserName: ad.advertiserName, vendorId: ad.vendorId ?? '', headline: ad.headline, plate: ad.plate, dealLabel: ad.dealLabel ?? '',
    permitText: ad.permitText ?? '', category: ad.category, clearanceCode: ad.clearanceCode ?? '', linkKind: ad.linkKind, linkTarget: ad.linkTarget ?? '',
    startDay: a.day, startTime: a.time, endDay: b.day, endTime: b.time, priority: String(ad.priority),
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const clean = (/** @type {string} */ s) => (s ?? '').replace(/\s+/g, ' ').trim();
const orNull = (/** @type {string} */ s) => (clean(s) ? clean(s) : null);

/**
 * Check the form and build the OpsAdInput.
 * @param {AdDraft} d
 * @returns {{ ok: true; value: OpsAdInput } | { ok: false; errors: Partial<Record<keyof AdDraft | 'schedule', string>> }}
 */
export function validateAd(d) {
  /** @type {Partial<Record<keyof AdDraft | 'schedule', string>>} */
  const errors = {};
  const advertiserName = clean(d.advertiserName);
  const headline = clean(d.headline);
  const dealLabel = orNull(d.dealLabel);
  const permitText = orNull(d.permitText);
  const clearanceCode = orNull(d.clearanceCode);
  if (!advertiserName) errors.advertiserName = 'Enter the advertiser, as the customer should see it.';
  else if (advertiserName.length > MAX_ADVERTISER) errors.advertiserName = `${MAX_ADVERTISER} characters at most.`;
  if (!headline) errors.headline = 'Write a headline.';
  else if (headline.length > MAX_HEADLINE) errors.headline = `${MAX_HEADLINE} characters at most (${headline.length} now).`;
  if (dealLabel && dealLabel.length > MAX_DEAL) errors.dealLabel = `${MAX_DEAL} characters at most, like "-30%" or "₱20 OFF".`;
  if (permitText && permitText.length > MAX_PERMIT) errors.permitText = `${MAX_PERMIT} characters at most.`;
  if (clearanceCode && clearanceCode.length > MAX_CLEARANCE) errors.clearanceCode = `${MAX_CLEARANCE} characters at most.`;
  if (!CATEGORY_OPTIONS.some((c) => c.value === d.category)) errors.category = 'Pick a category we accept.';
  if (!PLATE_KEYS.includes(d.plate)) errors.plate = 'Pick a plate colour.';
  if (d.kind !== 'photo' && d.kind !== 'video') errors.kind = 'Pick photo or video.';

  const target = clean(d.linkTarget);
  if (d.linkKind === 'shop' && !SLUG_RE.test(target)) errors.linkTarget = 'Pick the shop the ad opens.';
  else if (d.linkKind === 'product' && !UUID_RE.test(target)) errors.linkTarget = "Paste the product's ID (from Ops → Products).";
  else if (d.linkKind === 'line' && !LINE_TARGETS.some((l) => l.value === target)) errors.linkTarget = 'Pick the line the ad opens.';

  const startsAt = fromManila(d.startDay, d.startTime);
  const endsAt = fromManila(d.endDay, d.endTime);
  if (!startsAt || !endsAt) errors.schedule = 'Set when the ad starts and ends.';
  else if (Date.parse(endsAt) <= Date.parse(startsAt)) errors.schedule = 'The end has to come after the start.';

  const priority = Number(clean(d.priority));
  if (!/^-?\d+$/.test(clean(d.priority)) || !Number.isSafeInteger(priority) || priority < 0 || priority > 1000) errors.priority = 'A whole number from 0 to 1000.';

  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    value: {
      kind: d.kind, advertiserName, vendorId: orNull(d.vendorId), headline, plate: d.plate, dealLabel, permitText, category: d.category, clearanceCode,
      linkKind: d.linkKind, linkTarget: d.linkKind === 'none' ? null : target, startsAt: /** @type {string} */ (startsAt), endsAt: /** @type {string} */ (endsAt), priority,
    },
  };
}

/**
 * The PATCH body for an edit: only the fields that changed.
 * @param {OpsAd} ad @param {OpsAdInput} next @returns {Partial<OpsAdInput>}
 */
export function adPatch(ad, next) {
  /** @type {Record<string, unknown>} */
  const out = {};
  for (const k of /** @type {(keyof OpsAdInput)[]} */ (Object.keys(next))) {
    const a = ad[k], b = next[k];
    const same = k === 'startsAt' || k === 'endsAt' ? Date.parse(String(a)) === Date.parse(String(b)) : a === b;
    if (!same) out[k] = b;
  }
  return /** @type {Partial<OpsAdInput>} */ (out);
}

/** The status buttons an ad offers. @param {AdStatus} status */
export function statusActions(status) {
  /** @type {{ to: AdStatus; label: string }[]} */
  const out = [];
  if (status === 'draft' || status === 'paused') out.push({ to: 'live', label: 'Go live' });
  if (status === 'live') out.push({ to: 'paused', label: 'Pause' });
  if (status === 'archived') out.push({ to: 'draft', label: 'Back to draft' });
  else out.push({ to: 'archived', label: 'Archive' });
  return out;
}

/** The toast after a status change. @param {string} name @param {AdStatus} to */
export function statusToast(name, to) {
  if (to === 'live') return `${name} is live. It shows on Home while its schedule runs.`;
  if (to === 'paused') return `${name} is paused. It's off Home until you put it live again.`;
  if (to === 'archived') return `${name} is archived.`;
  return `${name} is a draft again.`;
}
